import type {
  EwellixPageNavigationComponentDTO,
  EwellixPageNavigationComponentProps,
} from "~/features/cms/components/cmsPageNavigation/types";
import { CMSButton } from "~/features/cms/components/cmsButton/CMSButton";
import type { CMSButtonProps } from "~/features/cms/types/props";
import ComponentTypes from "~/features/cms/componentTypes";
import {
  type CMSPageNavigationComponentProps,
  usePageNavigation,
} from "~/features/cms/components/cmsPageNavigation/CMSPageNavigation";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { Grid, Link, List, ListItemButton, Menu, Stack, styled, Typography } from "~/common/mui";
import {
  C_ON_SURFACE_LOW_EMPHASIS,
  C_ON_SURFACE_VARIANT,
  C_OUTLINE_VARIANT,
  C_PRIMARY,
  C_WHITE,
} from "~/common/components/icon/Colors";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { renderCMSComponent } from "~/features/cms/componentFactory";
import { Container } from "~/common/components/Layout";
import { STICKY_TO_BOTTOM } from "~/common/components/icon/Elevation";
import { useRef, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

export const EwellixPageNavigationComponent = (props: EwellixPageNavigationComponentDTO) => {
  const { t } = useTranslation();
  const {
    navigationButtons: navigationButtonProps,
    navigationItems,
    columnWidths,
    content,
    dropdownLabel,
  } = transformData(props);
  const screenSize = useNumericScreenSize();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const { activeIndex } = usePageNavigation(navigationItems);

  const handleNavigationClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setOpen(false);
    const element = document.querySelector(e.currentTarget.getAttribute("href") ?? "");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navigationList = (
    <List>
      {navigationItems.map((item, index) => (
        <StyledListItemButton
          key={item.href}
          // @ts-expect-error
          component={"a"}
          href={item.href}
          onClick={handleNavigationClick}
          selected={index === activeIndex}
        >
          {item.label}
        </StyledListItemButton>
      ))}
    </List>
  );

  const navigationButtons = navigationButtonProps.map((button, index) => (
    <CMSButton key={index} {...button} className={"w-100"} />
  ));

  if (screenSize >= useNumericScreenSize.desktopS) {
    return (
      <Grid container>
        <Grid sx={{ flexGrow: 1, width: 0 }}>
          <Stack
            sx={{
              gap: 8,
            }}
          >
            {content.map((item) => renderCMSComponent(item))}
          </Stack>
        </Grid>
        <Grid
          sx={{
            flexBasis: { "desktop-s": columnWidths[0], "desktop-m": columnWidths[1], "desktop-l": columnWidths[2] },
          }}
        >
          <Stack
            sx={{
              gap: 4,
              position: "sticky",
              top: "24px",
            }}
          >
            <Stack
              sx={{
                gap: 2,
              }}
            >
              <Typography sx={{ color: C_ON_SURFACE_LOW_EMPHASIS }}>
                {t("cms.component.ewellixPageNavigation.navTitle")}
              </Typography>
              {navigationList}
            </Stack>
            {navigationButtons}
          </Stack>
        </Grid>
      </Grid>
    );
  } else {
    const linkLabel = navigationItems[activeIndex]?.label ?? "MISSING LABEL";
    return (
      <>
        <StickyContainer>
          <FullbleedContainer>
            <Stack sx={{ gap: 1 }}>
              <Typography variant="bodyLarge" sx={{ fontWeight: "bold" }}>
                {dropdownLabel}
              </Typography>
              <Link
                component={"button"}
                underline={open ? "always" : "none"}
                ref={anchorRef}
                onClick={() => setOpen((prev) => !prev)}
                sx={{ color: C_ON_SURFACE_VARIANT, display: "inline-flex", alignItems: "center" }}
              >
                {linkLabel}
                <ExpandMoreIcon sx={{ fontSize: 18, marginLeft: "8px", transform: open ? "rotate(180deg)" : "" }} />
              </Link>
              <Menu
                anchorEl={anchorRef.current}
                open={open}
                onClose={() => setOpen(false)}
                slotProps={{ paper: { sx: { width: "100%", marginTop: 1 } } }}
              >
                <Stack sx={{ gap: 2, padding: 2 }}>
                  {navigationList}
                  {navigationButtons}
                </Stack>
              </Menu>
            </Stack>
          </FullbleedContainer>
        </StickyContainer>
        <Stack sx={{ gap: 8 }}>{content.map((item) => renderCMSComponent(item))}</Stack>
      </>
    );
  }
};

function transformData(dto: EwellixPageNavigationComponentDTO): EwellixPageNavigationComponentProps {
  const buttons = dto.navigation.filter((item): item is CMSButtonProps => item.type === ComponentTypes.CMSButton);
  const navigation = dto.navigation.filter(
    (item): item is CMSPageNavigationComponentProps => item.type === ComponentTypes.CMSPageNavigation,
  )[0];
  return {
    type: dto.type,
    content: dto.content,
    navigationButtons: buttons,
    navigationItems: navigation?.items ?? [],
    columnWidths: DEFAULT_COLUMN_WIDTHS.map((size) => size + "px") as [string, string, string],
    dropdownLabel: dto.mobileTitle,
  };
}

const DEFAULT_COLUMN_WIDTHS: [number, number, number] = [236, 306, 376];

const StyledListItemButton = styled(ListItemButton)({
  position: "relative",
  ...getFontStyle(FontSize.BodyLarge),
  fontWeight: "bold",
  ["&:after"]: {
    content: '""',
    position: "absolute",
    width: "3px",
    height: "100%",
    backgroundColor: C_OUTLINE_VARIANT,
    top: 0,
    left: 0,
  },
  color: C_ON_SURFACE_VARIANT,
  ["&.Mui-selected"]: {
    color: C_PRIMARY,
    background: "none",
    ["&:after"]: {
      backgroundColor: C_PRIMARY,
    },
  },
});

const StickyContainer = styled("div")({
  position: "sticky",
  top: 0,
  zIndex: 1,
});

const FullbleedContainer = styled(Container)({
  width: "100vw",
  position: "relative",
  marginLeft: "calc(var(--gutter) * -1)",
  marginRight: "calc(var(--gutter) * -1)",
  boxShadow: STICKY_TO_BOTTOM,
  background: C_WHITE,
  paddingBlock: "8px",
  marginBottom: "16px",
});
